// import node module libraries
import React, { useMemo } from "react";
import { ProgressBar, Badge } from "react-bootstrap";

// import custom components
import TanstackTable from "components/elements/advance-table/TanstackTable";
import { Avatar } from "components/elements/bootstrap/Avatar";
import moment from "moment";

const EnrolledUsersTable = ({ enrolledUsers }) => {

	const getProgressStatus = (progress) => {
		const prog = parseFloat(progress || 0);
		if (prog === 0) return { label: "Not Started", color: "secondary", icon: "fe-pause-circle" };
		if (prog < 25) return { label: "Just Started", color: "info", icon: "fe-play-circle" };
		if (prog < 50) return { label: "In Progress", color: "primary", icon: "fe-activity" };
		if (prog < 75) return { label: "Half Way", color: "warning", icon: "fe-trending-up" };
		if (prog < 100) return { label: "Almost Done", color: "info", icon: "fe-zap" };
		return { label: "Completed", color: "success", icon: "fe-check-circle" };
	};

	const columns = useMemo(
		() => [
			{
				header: "Student",
				accessorFn: row => `${row.user.firstName} ${row.user.lastName}`,
				cell: ({ row, getValue }) => (
					<div className="student-cell">
						<div className="d-flex align-items-center gap-3">
							<Avatar
								size="lg"
								className="rounded-circle student-avatar"
								type={row.original.user.image ? "image" : "initial"}
								src={row.original.user.image}
								alt={getValue()}
								name={getValue()}
							/>
							<div className="student-info">
								<h6 className="student-name mb-1">{getValue()}</h6>
								<div className="student-meta">
									<i className="fe fe-mail text-muted me-1"></i>
									<span className="student-email">{row.original.user.email}</span>
								</div>
							</div>
						</div>
					</div>
				),
			},
			{
				header: "Country",
				accessorKey: "user.country",
				cell: ({ getValue }) => {
					return (
						<div className="country-cell">
							{getValue() ? (
								<div className="d-flex align-items-center">
									<i className="fe fe-globe text-muted me-2"></i>
									<span>{getValue()}</span>
								</div>
							) : (
								<span className="text-muted">—</span>
							)}
						</div>
					);
				}
			},
			{
				accessorKey: "courseProgress",
				header: "Progress",
				cell: ({ getValue, row }) => {
					const progress = parseFloat(getValue() || 0);
					const status = getProgressStatus(progress);
					
					return (
						<div className="progress-cell">
							<div className="d-flex justify-content-between align-items-center mb-2">
								<Badge bg="" className={`progress-status-badge status-${status.color}`}>
									<i className={`fe ${status.icon} me-1`}></i>
									{status.label}
								</Badge>
								<span className="progress-percentage">{progress.toFixed(0)}%</span>
							</div>
							<ProgressBar
								now={progress}
								className="modern-progress-bar"
								variant={status.color}
							/>
						</div>
					);
				}
			},
			// {
			// 	accessorKey: "lastAccessedAt",
			// 	header: "Last Activity",
			// 	cell: ({ getValue, row }) => {
			// 		const lastAccessed = getValue();
			// 		if (!lastAccessed && !row.original.hasProgress) {
			// 			return (
			// 				<div className="activity-cell">
			// 					<Badge bg="" className="no-activity-badge">
			// 						<i className="fe fe-alert-circle me-1"></i>
			// 						No Activity
			// 					</Badge>
			// 				</div>
			// 			);
			// 		}
					
			// 		const date = lastAccessed || row.original.createdAt;
			// 		return (
			// 			<div className="activity-cell">
			// 				<div className="activity-primary">
			// 					<i className="fe fe-clock text-muted me-2"></i>
			// 					{moment(date).fromNow()}
			// 				</div>
			// 				<div className="activity-secondary">
			// 					{moment(date).format("MMM DD, YYYY")}
			// 				</div>
			// 			</div>
			// 		);
			// 	},
			// },
			{
				accessorKey: "createdAt",
				header: "Enrolled On",
				cell: ({ getValue }) => {
					return (
						<div className="enrolled-cell">
							<div className="enrolled-primary">
								<i className="fe fe-calendar text-muted me-2"></i>
								{moment(getValue()).format("MMM DD, YYYY")}
							</div>
							<div className="enrolled-secondary">
								{moment(getValue()).fromNow()}
							</div>
						</div>
					);
				},
			},
		],
		[]
	);

	const data = useMemo(() => enrolledUsers, [enrolledUsers]);

	return (
		<>
			<TanstackTable
				data={data}
				columns={columns}
				filter={true}
				filterPlaceholder="Search by student name, email, or phone..."
				pagination={true}
			/>

			<style jsx global>{`
				/* Student Cell */
				.student-cell {
					/* Handled by children */
				}

				.student-avatar {
					flex-shrink: 0;
				}

				.student-info {
					flex: 1;
					min-width: 0;
				}

				.student-name {
					font-size: 0.9375rem;
					font-weight: 700;
					color: #1e293b;
					margin: 0;
				}

				.student-meta {
					display: flex;
					align-items: center;
					font-size: 0.8125rem;
				}

				.student-email {
					color: #64748b;
					overflow: hidden;
					text-overflow: ellipsis;
					white-space: nowrap;
				}

				/* Contact Cell */
				.contact-cell {
					font-size: 0.875rem;
					color: #475569;
					font-weight: 500;
				}

				/* Progress Cell */
				.progress-cell {
					min-width: 250px;
				}

				.progress-status-badge {
					padding: 4px 10px;
					border-radius: 6px;
					font-size: 0.6875rem;
					font-weight: 700;
					text-transform: uppercase;
					letter-spacing: 0.5px;
					display: inline-flex;
					align-items: center;
				}

				.status-secondary {
					background: rgba(100, 116, 139, 0.1) !important;
					color: #64748b !important;
				}

				.status-info {
					background: rgba(29, 158, 176, 0.1) !important;
					color: #1d9eb0 !important;
				}

				.status-primary {
					background: rgba(59, 130, 246, 0.1) !important;
					color: #3b82f6 !important;
				}

				.status-warning {
					background: rgba(251, 191, 36, 0.1) !important;
					color: #fbbf24 !important;
				}

				.status-success {
					background: rgba(16, 185, 129, 0.1) !important;
					color: #10b981 !important;
				}

				.progress-percentage {
					font-size: 1rem;
					font-weight: 800;
					color: #1e293b;
				}

				.modern-progress-bar {
					height: 10px;
					border-radius: 10px;
					background-color: #e2e8f0;
				}

				.modern-progress-bar .progress-bar {
					border-radius: 10px;
					background: linear-gradient(90deg, currentColor 0%, currentColor 100%);
					transition: width 0.6s ease;
				}

				/* Activity Cell */
				.activity-cell {
					display: flex;
					flex-direction: column;
					gap: 0.25rem;
				}

				.no-activity-badge {
					background: rgba(239, 68, 68, 0.1) !important;
					color: #ef4444 !important;
					padding: 4px 10px;
					border-radius: 6px;
					font-size: 0.6875rem;
					font-weight: 700;
					text-transform: uppercase;
					letter-spacing: 0.5px;
					display: inline-flex;
					align-items: center;
				}

				.activity-primary {
					font-size: 0.875rem;
					color: #475569;
					font-weight: 600;
					display: flex;
					align-items: center;
				}

				.activity-secondary {
					font-size: 0.75rem;
					color: #94a3b8;
				}

				/* Enrolled Cell */
				.enrolled-cell {
					display: flex;
					flex-direction: column;
					gap: 0.25rem;
				}

				.enrolled-primary {
					font-size: 0.875rem;
					color: #475569;
					font-weight: 500;
					display: flex;
					align-items: center;
				}

				.enrolled-secondary {
					font-size: 0.75rem;
					color: #94a3b8;
				}

				/* Table Enhancements */
				.table {
					margin-bottom: 0;
				}

				.table thead {
					background: #f8fafc;
				}

				.table thead th {
					font-weight: 700;
					color: #475569;
					text-transform: uppercase;
					font-size: 0.75rem;
					letter-spacing: 0.5px;
					padding: 1rem 1.5rem;
					border-bottom: 2px solid #e2e8f0;
				}

				.table tbody tr {
					transition: all 0.2s ease;
				}

				.table tbody tr:hover {
					background: #f8fafc;
				}

				.table tbody td {
					padding: 1.25rem 1.5rem;
					vertical-align: middle;
				}

				/* Empty State */
				.table tbody tr td[colspan] {
					text-align: center;
					padding: 3rem;
					color: #94a3b8;
				}

				/* Responsive */
				@media (max-width: 768px) {
					.progress-cell {
						min-width: 200px;
					}

					.table thead th,
					.table tbody td {
						padding: 1rem;
					}

					.student-name {
						font-size: 0.875rem;
					}

					.student-meta {
						font-size: 0.75rem;
					}
				}
			`}</style>
		</>
	);
};

export default EnrolledUsersTable;